// Quote Builder — customer-facing line copy.
//
// ONE definition of "what a line says to the customer", shared by every
// customer-facing surface so they cannot drift apart:
//
//   • pdf.ts            — the retailer's own customer PDF (mode === "customer")
//   • actions.ts        — the payload sent to the customer portal, which is
//                         also what the server renders into the emailed PDF
//
// The internal / supplier documents do NOT come through here. They keep
// `it.desc` and `it.notes` verbatim (see pdfSpecLines + the internal branch of
// buildLinesTable, and place-order.ts for the PO) because the factory needs
// every one of the fragments this module strips.
//
// ── Why this exists ─────────────────────────────────────────────────────────
// The pickers encode manufacturing instructions into the two fields that reach
// the customer. A door line arrives as
//
//   "Mornington Shaker Dove Grey 715 × 497mm — Left hand base door
//    · 2 holes — top 98mm · bottom 98mm"
//
// and an SJB cabinet arrives with
//
//   "Board: KRONOSPAN · TORRO CREMONA OAK · PW | 45% board discount
//    | Hinges: Standard | Hinge side: Right | Front edging: EGGER · LIGHT GREY
//    · ST9 | Blanking panel: Right | CUT TO SIZE: depth 330mm (standard 300mm)
//    | Inset blanking panel"
//
// in `notes`. Hole offsets, hinge sides, blanking panels, cut-to-size
// deviations and the retailer's own board discount are all things the customer
// should never be handed — one of them is commercially sensitive and the rest
// are noise that made an 84-line quote render at ~22mm per row.

import type { QuoteItem } from "./types";

/**
 * Note labels a customer is entitled to see. WHITELIST, deliberately — a
 * blocklist would leak every fragment a future picker invents.
 *
 * Everything not on this list stays on the internal copy: "45% board discount"
 * (our margin), "Hinges", "Hinge side", "Blanking panel", "CUT TO SIZE",
 * "Frontal for <code>", "Door 1 of 2", "positions on each door line".
 */
const CUSTOMER_SPEC_LABELS = new Map<string, string>([
  ["board",             "Board"],
  ["carcass",           "Carcass"],
  ["carcase",           "Carcass"],
  ["front edging",      "Edging"],
  ["edging",            "Edging"],
  ["worktop",           "Worktop"],
  ["handle",            "Handle"],
  ["integrated handle", "Handle"],
  ["finish",            "Finish"],
  ["colour",            "Colour"],
  ["door colour",       "Colour"],
]);

/** At most this many spec pairs on one line — past four it stops being a
 *  summary and starts being the note block again. */
const MAX_SPEC_PARTS = 4;

/**
 * The line description as the customer should read it.
 *
 * Strips the two fabrication clauses the door pickers append, and nothing
 * else — the rest of the description is the product name and is preserved
 * verbatim, including anything a retailer typed by hand.
 *
 *   "… 715 × 497mm — Left hand base door · 2 holes — top 98mm · bottom 98mm"
 *   → "… 715 × 497mm — base door"
 *
 * Deliberately a strip, not a rebuild from `doorRange`/`doorColour`/`width`:
 * a rebuild would silently drop whatever a picker put in the description that
 * has no structured field behind it (glazing, grain direction, bespoke text),
 * and would quietly paper over description-assembly bugs instead of leaving
 * them visible where they get fixed.
 */
export function customerLineDescription(it: QuoteItem): string {
  let d = String(it.desc ?? "").trim();
  // "· 2 holes — top 98mm · bottom 98mm" and any other trailing hole clause.
  d = d.replace(/\s*·\s*\d+\s*holes?\b.*$/i, "");
  // Hinge side is a fitting instruction, not a property of the product.
  // Trailing whitespace is OPTIONAL: the description is trimmed before we get
  // here, so a desc that ENDS in the handing ("… Dove Grey — Right hand") has
  // nothing after "hand" to match, and a required `\s+` left it in place.
  d = d.replace(/\b(?:left|right)\s+hand\b\s*/gi, "");
  // An SJB cabinet carries its doors and hinge side in the description —
  // "900mm Tall Wall LH & RH · 2 × 895 × 447, 2 holes" — for the same reason
  // a door carries its handing: it is a fitting instruction. Cabinets only,
  // so no other product's "· 2 × …" text is touched.
  if (it.category === "cabinets") {
    d = d.replace(/\s*·\s*\d+\s*×\s*\d+.*$/, "");
    d = d.replace(/\s+(?:LH|RH)(?:\s*&\s*(?:LH|RH))?$/, "");
  }
  return d
    .replace(/\s{2,}/g, " ")
    .replace(/\s*[—·|-]\s*$/, "")
    .trim();
}

/**
 * One line of customer-relevant spec: size, board/carcass colour, edging.
 *
 * Values are de-duplicated on the VALUE, not the label, because the same
 * edging colour reaches us twice — once as the structured `it.edging` and once
 * as a "Front edging:" note fragment — and printing it twice is exactly the
 * doubling the old bullet-plus-note layout produced.
 *
 * Returns "" when there is nothing worth saying, so callers can omit the line
 * entirely rather than print an empty second row.
 */
export function customerSpecSummary(it: QuoteItem): string {
  const parts: string[] = [];
  const seen = new Set<string>();

  const push = (label: string, value: unknown): void => {
    if (parts.length >= MAX_SPEC_PARTS) return;
    const v = String(value ?? "").trim();
    if (!v) return;
    const key = v.toLowerCase().replace(/[^a-z0-9]+/g, "");
    if (!key || seen.has(key)) return;
    seen.add(key);
    parts.push(label ? `${label}: ${v}` : v);
  };

  // Size, for the categories where the description does not already carry it.
  if (it.category === "cabinets" && it.height && it.width && it.depth) {
    push("", `${it.height} × ${it.width} × ${it.depth}mm`);
  } else if (it.category === "worktops") {
    push("", it.material);
  }

  push("Carcass", it.carcassFinish || it.carcaseColour);
  push("Edging", it.edging);

  for (const frag of String(it.notes ?? "").split("|")) {
    const m = /^\s*([A-Za-z][A-Za-z ]{0,24}):\s*(.+)$/.exec(frag);
    if (!m) continue;
    const label = CUSTOMER_SPEC_LABELS.get(m[1].trim().toLowerCase());
    if (!label) continue;
    push(label, m[2]);
  }

  return parts.join(" · ");
}
